import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Loader2, Check, Crown, Clock, Eye, ArrowRightLeft, Heart, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";

export const prettyDate = (iso) =>
  new Date(`${iso}T12:00:00`).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });

export const isHeated = (day) => day === "Friday" || day === "Saturday";

export const shortDate = (iso) => {
  const [y, m, d] = iso.split("-");
  return `${Number(m)}/${Number(d)}/${y.slice(2)}`;
};

const NameNudge = ({ user, setUser }) => {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [state, setState] = useState("idle");
  useEffect(() => {
    api.patch("/me/preferences", { mat_name_nudge_seen: true }).then(({ data }) => setUser(data)).catch(() => {});
  }, [setUser]);
  const enable = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/me/preferences", { show_name_on_mat: true });
      setUser(data); setState("on");
      toast.success("Your name is now visible on your reserved mats.");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  if (state === "on") {
    return (
      <p data-testid="name-nudge-enabled" className="mt-6 text-xs text-neon">
        Name sharing is on — classmates will now see "{user.mat_display_name || user.name?.split(" ")[0]}" on your mat. Change it anytime in your account.
      </p>
    );
  }
  return (
    <div data-testid="name-nudge" className="mt-6 border border-neon/40 bg-neon/5 p-4 text-left">
      <p className="text-[10px] uppercase tracking-[0.25em] text-neon mb-1 flex items-center gap-1.5"><Eye size={12} /> Pssst — one-time tip</p>
      <p className="text-sm text-white font-medium">Want classmates to spot you on the mat map?</p>
      <p className="text-xs text-neutral-400 mt-1">
        Turn on <span className="text-neutral-200">Show My Name on Mat Reservations</span> so other clients booked in your class
        can see your first name on your mat. It's off by default and you can switch it off anytime.
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        <button onClick={enable} disabled={saving} data-testid="name-nudge-enable"
          className="inline-flex items-center gap-1.5 bg-neon text-black font-semibold text-[11px] uppercase tracking-[0.15em] px-4 py-2 hover:bg-white transition-colors disabled:opacity-50">
          {saving ? <Loader2 size={12} className="animate-spin" /> : <Eye size={12} />} Turn it on
        </button>
        <button onClick={() => navigate("/account")} data-testid="name-nudge-account"
          className="text-[11px] uppercase tracking-[0.15em] border border-neutral-700 px-4 py-2 text-neutral-400 hover:border-neon hover:text-neon transition-colors">
          Manage in account
        </button>
      </div>
    </div>
  );
};

const Mat = ({ n, taken, mine, name, selected, disabled, onSelect }) => {
  const state = mine ? "mine" : taken ? "taken" : selected ? "sel" : "open";
  const label = mine ? "You" : taken ? name : n;
  return (
    <button
      type="button"
      disabled={disabled || taken || mine}
      data-testid={`mat-spot-${n}`}
      data-state={state}
      onClick={() => onSelect(n)}
      aria-label={mine ? `Mat ${n} — your reservation` : taken ? `Mat ${n} — reserved by ${name || "another client"}` : `Mat ${n} — available`}
      aria-pressed={selected}
      title={mine ? `Mat ${n} — your mat` : taken ? `Mat ${n} — ${name || "Reserved"}` : `Mat ${n}`}
      className={`mat-cell aspect-square w-full min-w-0 flex items-center justify-center font-medium border overflow-hidden px-px text-center
        ${state === "taken" || state === "mine" ? "text-[8px] sm:text-[10px] leading-tight tracking-tight" : "text-sm"}
        ${state === "taken" ? "border-neutral-800 bg-neutral-950 text-neutral-500 cursor-not-allowed" : ""}
        ${state === "mine" ? "border-white bg-white text-black" : ""}
        ${state === "sel" ? "border-neon bg-neon text-black font-semibold neon-glow scale-105" : ""}
        ${state === "open" ? "border-neutral-800 text-neutral-400 hover:border-neutral-500 hover:text-white" : ""}
        ${disabled && state === "open" ? "opacity-40 cursor-not-allowed hover:border-neutral-800 hover:text-neutral-400" : ""}`}
    >
      {state === "taken" && !name ? (
        <X data-testid={`mat-label-${n}`} className="w-[45%] h-[45%] text-neutral-600" strokeWidth={2.5} aria-hidden="true" />
      ) : (
        <span data-testid={`mat-label-${n}`} className="block max-w-full truncate">{label}</span>
      )}
    </button>
  );
};

// cls: {class_id, class_name, day, time, instructor}; date: ISO or null (next occurrence)
export const MatMapDialog = ({ cls, date, onClose, onChanged }) => {
  const { user, setUser } = useAuth();
  const [mats, setMats] = useState(null);
  const [matsError, setMatsError] = useState("");
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [moved, setMoved] = useState(null);
  const [crashed, setCrashed] = useState(null);
  const [showNudge, setShowNudge] = useState(false);
  const [favNote, setFavNote] = useState(null);
  const autoPickRef = useRef(false);
  const open = !!cls;

  const loadMats = useCallback(async () => {
    if (!cls) return;
    try {
      const { data } = await api.get(`/classes/${cls.class_id}/mats${date ? `?date=${date}` : ""}`);
      setMats(data); setMatsError("");
    } catch (e) {
      setMatsError(apiError(e.response?.data?.detail) || "Couldn't load the mats — please close and try again.");
    }
  }, [cls, date]);

  useEffect(() => {
    if (!open) return;
    setMats(null); setMatsError(""); setSelected(null); setDone(false); setMoved(null); setCrashed(null); setFavNote(null); setShowNudge(false);
    autoPickRef.current = true;
    loadMats();
  }, [open, loadMats]);

  useEffect(() => {
    if (!mats || !autoPickRef.current) return;
    autoPickRef.current = false;
    const fav = user?.favorite_mat;
    if (!fav || mats.my_spot) { setFavNote(null); return; }
    if (!mats.taken.includes(fav) && mats.can_book) { setSelected(fav); setFavNote({ spot: fav, open: true }); }
    else setFavNote({ spot: fav, open: false });
  }, [mats, user]);

  useEffect(() => {
    if (open && !done && !matsError) {
      const id = setInterval(loadMats, 4000);
      return () => clearInterval(id);
    }
  }, [open, done, matsError, loadMats]);

  const submit = async () => {
    if (!selected) return toast.error("Pick a mat first.");
    setLoading(true);
    try {
      const { data } = await api.post("/bookings", { class_id: cls.class_id, spot: selected, ...(date ? { date } : {}) });
      setDone(true);
      setShowNudge(!!user && user.role !== "admin" && !user.show_name_on_mat && !user.mat_name_nudge_seen);
      if (data?.note) toast.info(data.note, { duration: 9000 });
      toast.success(`Mat ${selected} locked in!${data?.credit_used ? " 1 class credit used." : ""}`);
      onChanged?.();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
      await loadMats();
      setSelected(null);
    } finally {
      setLoading(false);
    }
  };

  const moveMat = async () => {
    if (!selected || !mats?.my_booking_id) return;
    setLoading(true);
    try {
      const { data } = await api.post(`/bookings/${mats.my_booking_id}/move`, { spot: selected });
      setMoved({ from: data.previous_spot, to: data.spot });
      setDone(true);
      toast.success(`Moved to mat ${data.spot}!`);
      onChanged?.();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
      await loadMats();
      setSelected(null);
    } finally {
      setLoading(false);
    }
  };

  const crash = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/bookings/crash", { class_id: cls.class_id, ...(date ? { date } : {}) });
      setCrashed(data.spot);
      setDone(true);
      toast.success(`VIP crash confirmed — overflow spot ${data.spot} is yours!`);
      onChanged?.();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
      await loadMats();
    } finally {
      setLoading(false);
    }
  };

  const canBook = mats?.can_book;
  const isVip = user && (user.tier === "vip" || user.role === "admin" || user.is_instructor);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="booking-dialog" className="bg-[#0A0A0A] border-neutral-800 text-white max-w-lg">
        {cls && !done && (
          <>
            <DialogHeader>
              <DialogTitle className="font-display uppercase text-3xl">{cls.class_name}</DialogTitle>
              <DialogDescription className="text-neutral-400">
                {date ? prettyDate(date) : cls.day} · {cls.time} · with {cls.instructor}
                {isHeated(cls.day) && <span className="text-orange-400"> · Heated class</span>}
              </DialogDescription>
            </DialogHeader>

            {matsError && (
              <div data-testid="mats-error" className="py-8 px-4 text-center">
                <p className="text-sm text-neutral-200 mb-4">{matsError}</p>
                <button onClick={onClose} data-testid="mats-error-close"
                  className="border border-neutral-700 text-xs uppercase tracking-[0.2em] px-6 py-3 hover:border-neon hover:text-neon transition-colors">
                  Back to schedule
                </button>
              </div>
            )}
            {!mats && !matsError && <div className="py-10 flex justify-center"><Loader2 className="animate-spin text-neon" /></div>}

            {mats && !canBook && (
              <div data-testid="vip-gate" className="border border-neon/40 bg-neon/5 p-6 text-center my-2">
                <Clock size={28} className="text-neon mx-auto mb-3" />
                <p className="font-display uppercase text-2xl mb-2">Booking opens {mats.next_month_opens ? prettyDate(mats.next_month_opens) : "next month"}</p>
                <p className="text-sm text-neutral-400">
                  Classes can be booked through {mats.booking_through ? prettyDate(mats.booking_through) : "the end of the current month"}. Next month opens on the last day of this month{mats.next_month_opens ? ` (${prettyDate(mats.next_month_opens)})` : ""} — come back then to grab this mat.
                </p>
              </div>
            )}

            {mats && (
              <div className="mt-1 min-w-0 w-full">
                <p className="text-[11px] uppercase tracking-[0.25em] text-neon text-center mb-2 flex items-center justify-center gap-2">
                  <Clock size={12} /> Front row — closest to instructor
                </p>
                {(mats.rows || []).map((row, ri) => {
                  const half = Math.ceil(row.length / 2);
                  const matEl = (n) => (
                    <Mat key={n} n={n} taken={mats.taken.includes(n)} mine={mats.my_spot === n} name={mats.names?.[n]}
                      selected={selected === n} disabled={!canBook} onSelect={setSelected} />
                  );
                  return ri === 0 ? (
                    <div key={ri} className="grid grid-cols-9 gap-1.5 mb-2 items-stretch" data-testid="mat-row-front">
                      {row.slice(0, half).map(matEl)}
                      <div data-testid="instructor-podium" className="aspect-square w-full flex flex-col items-center justify-center border border-neon/50 bg-neon/10 text-neon">
                        <Crown size={16} />
                        <span className="text-[8px] uppercase tracking-wider mt-0.5">Coach</span>
                      </div>
                      {row.slice(half).map(matEl)}
                    </div>
                  ) : (
                    <div key={ri} className="grid grid-cols-8 gap-1.5 mb-1.5" data-testid={`mat-row-${ri + 1}`}>
                      {row.map(matEl)}
                    </div>
                  );
                })}
                {(mats.rows || []).length > 1 && (
                  <p className="text-[11px] uppercase tracking-[0.25em] text-neutral-400 text-center mt-2">Back row{mats.rows.length > 2 ? "s" : ""}</p>
                )}

                {favNote && !mats.my_spot && canBook && (
                  <p data-testid={favNote.open ? "fav-preselected" : "fav-taken"}
                    className={`mt-3 text-xs text-center inline-flex w-full items-center justify-center gap-1.5 ${favNote.open ? "text-neon" : "text-neutral-500"}`}>
                    <Heart size={11} fill={favNote.open ? "currentColor" : "none"} aria-hidden="true" />
                    {favNote.open
                      ? `Your favorite mat ${favNote.spot} is open — pre-selected for you.`
                      : `Your favorite mat ${favNote.spot} is already taken — pick another spot.`}
                  </p>
                )}

                {mats.my_spot && (
                  <div className="mt-3 text-center" data-testid="my-mat-panel">
                    <p className="text-sm text-white">You're on mat {mats.my_spot} for this class.</p>
                    {mats.can_move ? (
                      <p className="text-xs text-neutral-400 mt-1" data-testid="move-hint">
                        Want a different spot? Tap any open mat below to move.
                      </p>
                    ) : (
                      <p className="text-xs text-neutral-500 mt-1" data-testid="move-closed">
                        Mat changes close 5 hours before class — ask the front desk when you arrive.
                      </p>
                    )}
                  </div>
                )}

                {mats.my_spot && mats.can_move && (
                  <button
                    onClick={moveMat}
                    disabled={loading || !selected}
                    data-testid="move-mat-submit"
                    className="mt-5 w-full inline-flex items-center justify-center gap-2 border-2 border-neon text-neon font-semibold text-xs uppercase tracking-[0.2em] py-4 hover:bg-neon hover:text-black transition-colors disabled:opacity-50 disabled:hover:bg-transparent disabled:hover:text-neon"
                  >
                    {loading ? <Loader2 size={16} className="animate-spin" /> : <ArrowRightLeft size={14} />}
                    {selected ? `Move to Mat ${selected}` : "Select a new mat"}
                  </button>
                )}

                {mats.full && !mats.my_spot && canBook && (
                  mats.can_crash ? (
                    <div data-testid="crash-panel" className="mt-5 border border-neon/40 bg-neon/5 p-4 text-center">
                      <p className="text-sm text-white mb-1 inline-flex items-center gap-1.5"><Crown size={14} className="text-neon" /> Every mat is taken — but you're VIP.</p>
                      <p className="text-xs text-neutral-400 mb-4">
                        Crash the class for an overflow spot (no numbered mat). {mats.crash_cap - mats.crashers} of {mats.crash_cap} left. The instructor will seat you on a mat when you arrive.
                      </p>
                      <button onClick={crash} disabled={loading} data-testid="crash-submit"
                        className="w-full inline-flex items-center justify-center gap-2 bg-neon text-black font-semibold text-xs uppercase tracking-[0.2em] py-4 hover:bg-white hover:text-black transition-colors disabled:opacity-50">
                        {loading ? <Loader2 size={16} className="animate-spin" /> : <Crown size={14} />} Crash this class
                      </button>
                    </div>
                  ) : (
                    <p data-testid="class-full-note" className="mt-5 text-xs text-center text-neutral-400 border border-neutral-800 p-3">
                      {isVip
                        ? `Every mat is taken and all ${mats.crash_cap} VIP overflow spots are full — join the waitlist from the schedule.`
                        : "Every mat is taken — join the waitlist from the schedule and we'll ping you when one opens."}
                    </p>
                  )
                )}

                {canBook && !mats.my_spot && !mats.full && (
                  <button
                    onClick={submit}
                    disabled={loading || !selected}
                    data-testid="booking-submit"
                    className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-neon text-black font-semibold text-xs uppercase tracking-[0.2em] py-4 hover:bg-white hover:text-black transition-colors disabled:opacity-50"
                  >
                    {loading ? <Loader2 size={16} className="animate-spin" /> : null}
                    {selected ? `Reserve Mat ${selected}` : "Select a mat"}
                  </button>
                )}

                <button type="button" onClick={onClose} data-testid="mat-map-close"
                  className="mt-3 w-full inline-flex items-center justify-center gap-2 border border-neutral-800 text-neutral-300 text-xs uppercase tracking-[0.2em] py-3.5 hover:border-neon hover:text-neon transition-colors touch-manipulation">
                  <X size={14} aria-hidden="true" /> Close
                </button>
              </div>
            )}
          </>
        )}

        {cls && done && (
          <div className="py-6 text-center" data-testid={moved ? "move-success" : crashed ? "crash-success" : "booking-success"}>
            <div className="mx-auto w-16 h-16 rounded-full bg-neon/15 flex items-center justify-center mb-5 neon-glow">
              {crashed ? <Crown size={30} className="text-neon" /> : <Check size={30} className="text-neon" />}
            </div>
            <h3 className="font-display uppercase text-3xl mb-2">{moved ? `You're now on mat ${moved.to}` : crashed ? "You crashed the class" : `Mat ${selected} is yours`}</h3>
            <p className="text-neutral-400 text-sm">
              {moved ? `Moved from mat ${moved.from}. ` : crashed ? `Overflow spot ${crashed} — no numbered mat, just bring your energy. ` : ""}{cls.class_name} · {date ? prettyDate(date) : `${cls.day} ${cls.time}`}{date ? ` · ${cls.time}` : ""}. See you on the floor!
            </p>
            {showNudge && <NameNudge user={user} setUser={setUser} />}
            <button onClick={onClose} data-testid="booking-close"
              className="mt-6 text-xs uppercase tracking-[0.2em] border border-neutral-700 px-6 py-3 hover:border-neon hover:text-neon transition-colors">
              Done
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
